import {useCallback, useEffect, useRef, useState} from "react";
import {CURRENCIES} from "../curify/constants";

const FLEXECOM_ENVIRONMENT = "development";

const DEFAULT_BILLING_ADDRESS = {
  line_1: "123 Example Rd.",
  city: "New York",
  state: "New York",
  postal_code: "10001",
  country_code: "US",
};

const CARD_FIELDS_STYLE = {
  fonts: ["https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700"],
  styles: {
    body: { fontFamily: "Poppins, sans-serif" },
    label: { display: "none" },
    "[ev-component=card]": {
      display: "grid",
      gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)",
      gap: "8px",
    },
    ".field[ev-name=name]": {
      gridColumn: "1 / -1",
      gridRow: "1",
      minWidth: 0,
    },
    ".field[ev-name=number]": {
      gridColumn: "1",
      gridRow: "2",
      minWidth: 0,
    },
    ".field[ev-name=expiry]": {
      gridColumn: "2",
      gridRow: "2",
      minWidth: 0,
    },
    ".field[ev-name=cvc]": {
      gridColumn: "3",
      gridRow: "2",
      minWidth: 0,
    },
    input: {
      boxSizing: "border-box",
      width: "100%",
      fontFamily: "Poppins, sans-serif",
      fontSize: 14,
      height: 56,
      padding: "0 16px",
      borderRadius: 6,
      color: "#3f4254",
      border: "1px solid #6f55ff",
      backgroundColor: "#6f55ff0d",
      "&::placeholder": { color: "#8a8aa3" },
      "&:focus": {
        outline: "none",
        backgroundColor: "#fff",
        borderColor: "#6f55ff",
      },
    },
    ".field[ev-valid=false] input": {
      borderColor: "#fa6160",
      color: "#fa6160",
    },
    ".error": {
      left: 0,
      top: "100%",
      color: "#fa6160",
      fontSize: "12px",
      padding: "2px 0 0",
    },
    ".field": {
      position: "relative",
    },
  },
};

const DEMO_PRODUCT_NAMES = [
  "Sample Product Title",
  "Another Product Title",
  "Third Product Title",
  "Fourth Product Title",
  "Fifth Product Title",
];

function randomProducts(amount) {
  const totalCents = Math.round(parseFloat(amount) * 100);
  if (!Number.isFinite(totalCents) || totalCents <= 0) {
    return [];
  }

  const count = Math.min(1 + Math.floor(Math.random() * 4), totalCents);

  const cuts = new Set();
  while (cuts.size < count - 1) {
    cuts.add(1 + Math.floor(Math.random() * (totalCents - 1)));
  }
  const bounds = [0, ...[...cuts].sort((a, b) => a - b), totalCents];

  const names = [...DEMO_PRODUCT_NAMES].sort(() => Math.random() - 0.5);

  return bounds.slice(1).map((bound, i) => {
    const lineCents = bound - bounds[i];
    const quantities = [1, 2, 3].filter((q) => lineCents % q === 0);
    const quantity = quantities[Math.floor(Math.random() * quantities.length)];

    return {
      quantity,
      sales_tax: 0.0,
      product_id: String(1000 + Math.floor(Math.random() * 9000)),
      product_name: names[i],
      product_type: "ONE_TIME",
      unit_price_amount: lineCents / quantity / 100,
      product_description: "Sample product description.",
    };
  });
}

function App() {
  const [publicKey, setPublicKey] = useState("pk_live_njLP8FKcMfaFMcByhDGtug");
  const [reference, setReference] = useState(
    Math.floor(Math.random() * 10000000)
  );
  const [descriptor, setDescriptor] = useState("StatementDescrip");
  const [firstName, setFirstName] = useState("John");
  const [lastName, setLastName] = useState("Smith");
  const [email, setEmail] = useState("john.smith@example.com");
  const [phone, setPhone] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  // null until chosen. "redirect": no card fields, submit() goes straight to
  // the hosted page. "capture": card fields here first, then the hosted page.
  const [flow, setFlow] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const [amount, setAmount] = useState(Math.floor(Math.random() * 1000));
  const [currency, setCurrency] = useState("USD");

  const [events, setEvents] = useState([]);

  const flexecomRef = useRef(null);

  const mountFields = useCallback(() => {
    if (!window.Flexecom) {
      // eslint-disable-next-line no-console
      console.error("window.Flexecom is not defined — ensure Flexecom.js loaded in public/index.html");
      return;
    }

    flexecomRef.current?.unmount();
    setCardComplete(false);

    flexecomRef.current = window.Flexecom.init({
      public_key: publicKey,
      flow: "hosted",
      ...(flow === "capture" ? { containerId: "card-fields", expressContainerId: "express-checkout" } : {}),
      hostedPaymentPageUrl: "https://pay.flexecom.dev",
      returnUrl: `${window.location.origin}/hosted-payment-return`,
      branding: {
        merchantName: "Forma Studio",
        logoUrl: `${window.location.origin}/logo.png`,
        summaryBackgroundColor: "#F4F0EC",
        checkoutBackgroundColor: "#ffffff",
        primaryColor: "#6f55ff",
      },
      metadata: {
        order_id: reference,
        order_date: "2026-10-01",
        order_type: "NEW_SALE",
        pay_source: "CREDITCARD",
        campaign_id: "3",
        order_number: reference,
        campaign_name: "Basic Campaign",
        recycle_number: 0,
        source_value_1: null,
        source_value_2: null,
        source_value_3: null,
        source_value_4: null,
        source_value_5: null,
        initial_order_id: reference,
        device_information: {
          ip_address: "123.456.789.101",
          device_name: "Android",
          device_type: "Mobile Phone"
        },
        order_total_amount: amount,
        products_purchased: randomProducts(amount),
        billing_cycle_number: 1,
        order_total_currency_code: currency
      },
      environment: FLEXECOM_ENVIRONMENT,
      reference: String(reference),
      style: CARD_FIELDS_STYLE,
      billing_address: DEFAULT_BILLING_ADDRESS,
      amount: String(amount),
      currencyCode: currency,
      descriptor: descriptor || null,

      onFieldsUpdated: (state) => setCardComplete(state.complete),

      onLoading: (loading) => setIsLoading(loading),

      onExpressCheckoutReady: () => {
        // eslint-disable-next-line no-console
        console.log("Express checkout ready");
      },
      onExpressCheckoutUnavailable: () => {
        // eslint-disable-next-line no-console
        console.log("Express checkout unavailable — amount/currencyCode missing at init");
      },

      onPaymentSuccess: (result) => {
        setEvents((prev) => [
          ...prev,
          { status: "completed", id: result.id, amount: result.amount, reference: result.reference },
        ]);
        setReference(Math.floor(Math.random() * 10000000))
        setAmount(Math.floor(Math.random() * 1000))
      },
      onPaymentFailure: () => {
        setEvents((prev) => [...prev, { status: "failed" }]);
        setReference(Math.floor(Math.random() * 10000000))
      },
      onError: (error) => {
        alert("Oops: " + error.message);
      },
    });
  }, [publicKey, reference, amount, currency, descriptor, flow]);

  // Mount once a flow is chosen (the card fields container only exists then)
  // and tear down when going back to the chooser.
  useEffect(() => {
    if (!flow) {
      return;
    }

    mountFields();

    return () => {
      flexecomRef.current?.unmount();
      flexecomRef.current = null;
      setCardComplete(false);
    };
  }, [flow, currency]);

  const processPayment = () => {
    setEvents([]);
    flexecomRef.current?.submit({
      amount: String(amount),
      currencyCode: currency,
      descriptor: descriptor || null,
    });
  };

  const renderCards = () => {
    return (
      <>
        <div className="mw-325px">
          <h3>Hosted Payment Page Portal</h3>
          <p className="mb-0">
            {flow
              ? "To test the payment, please fill in the required details on the right side"
              : "Choose how the payment should be taken to get started"}
          </p>
        </div>
      </>
    )
  }

  const FLOWS = [
    {
      id: "redirect",
      title: "Redirect only",
      description: "No card details on this page. Submit sends the customer straight to the hosted payment page to enter their card and pay.",
    },
    {
      id: "capture",
      title: "Capture card here, then redirect",
      description: "The customer enters their card on this page first. Submit then sends them to the hosted payment page, which completes the payment without asking for card details again.",
    },
  ];

  const renderFlowChooser = () => {
    return FLOWS.map(({ id, title, description }) => (
      <div className={"s-card"} key={id}>
        <label className="form-label">{title}</label>
        <div className="form-text mb-4">{description}</div>
        <button
          className="btn btn-primary w-100"
          type="button"
          onClick={() => setFlow(id)}
        >
          Continue
        </button>
      </div>
    ));
  }

  const renderEvents = () => {
    return events.map((event, i) => {

      if(event.status === "completed"){
        return (
          <div className="alert alert-success mb-0 text-start">
            <h6>Payment Successful                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      !</h6>
            <div>Status: Success</div>
            <div>Next Action: Notify client of payment success</div>
          </div>
        )
      }else if(event.status === "failed"){
        return (
          <div className="alert alert-danger mb-0 text-start">
            <h6>Payment Failed</h6>
            <div>Status: Failed</div>
            <div>Next Action: None, no applicable processors to retry payment on</div>
          </div>
        )
      }

      return null
    })
  }

  return (
    <div className="page demo">
      <div className="onboarding">
        <div className="onb-aside w-100">
          <div className="t-section">
            <a href="javascript:void(0)">
              <img alt="Logo" src="logo.png" className="h-40px"/>
            </a>
            {(!isLoading && events.length === 0) && renderCards()}
            {renderEvents()}
            {isLoading && (
              <>
                <div className="alert mb-0">
                  <h6>Loading...</h6>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="onb-body">
          <div className={"form"}>
            {!flow && renderFlowChooser()}
            {flow && (<>
            <div className={"s-card"}>
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label mb-0">
                  {FLOWS.find((f) => f.id === flow).title}
                </label>
                <button
                  className="btn btn-light btn-sm"
                  type="button"
                  disabled={isLoading}
                  onClick={() => setFlow(null)}
                >
                  Change flow
                </button>
              </div>
            </div>

            {/* Starts: Express Checkout */}
            {flow === "capture" && (
              <div className={"s-card"}>
                <label className="form-label">Express Checkout</label>
                <div id={"express-checkout"} />
              </div>
            )}
            {/* End: Express Checkout */}

            {/* Starts: Merchant Details */}
            <div className={"s-card"}>
              <label className="form-label">Merchant</label>
              {/* Starts: Merchant Public Key */}
              <div className="form-group">
                <label className="form-label fw-normal">
                  Merchant Public Key*
                </label>
                <input
                  className="form-control"
                  placeholder="Merchant Public Key"
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.target.value || null)}
                  onBlur={mountFields}
                />
                <div className="form-text">
                  Defines the Merchant the transaction is processed on behalf of.
                </div>
              </div>
              {/* end: Merchant Public Key */}
              {/* Starts: Merchant Reference */}
              <div className="form-group">
                <label className="form-label fw-normal">
                  Merchant Reference*
                </label>
                <input
                  className="form-control"
                  placeholder="Merchant Reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value || null)}
                  onBlur={mountFields}
                />
                <div className="form-text">
                  Merchant's reference for the payment.
                </div>
              </div>
              {/* end: Merchant Reference */}
              {/* Starts: Bank Descriptor */}
              <div className="form-group">
                <label className="form-label fw-normal">
                  Bank Descriptor
                </label>
                <input
                  className="form-control"
                  placeholder="Bank Descriptor"
                  value={descriptor}
                  onChange={(e) => setDescriptor(e.target.value)}
                  onBlur={mountFields}
                  maxLength={18}
                />
                <div className="form-text">
                  Statement descriptor shown to the customer on their bank statement.
                </div>
              </div>
              {/* end: Bank Descriptor */}
              {/* Starts: Amount,Currency  */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Amount*</label>
                    <input
                      className="form-control"
                      placeholder="Amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value || null)}
                      onBlur={mountFields}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Currency*</label>
                    <select
                      className="form-control form-select"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      {CURRENCIES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {/* end: Amount,Currency */}
            </div>
            {/* End: Merchant Details */}

            {/* Starts: Customer Details */}
            <div className={"s-card"}>
              <label className="form-label">Customer Details</label>
              {/* Starts: First and Last Names */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">First Name</label>
                    <input
                      className="form-control"
                      placeholder="first_name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value || null)}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Last Name</label>
                    <input
                      className="form-control"
                      placeholder="last_name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value || null)}
                    />
                  </div>
                </div>
              </div>
              {/* end: First and Last Names */}
              {/* Starts: Email and Phone */}
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Email</label>
                    <input
                      className="form-control"
                      placeholder="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value || null)}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="form-group">
                    <label className="form-label fw-normal">Phone</label>
                    <input
                      className="form-control"
                      placeholder="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value || null)}
                    />
                  </div>
                </div>
              </div>
              {/* end: Email and Phone */}
            </div>
            {/* End: Customer Details */}

            {/* Starts: Card Details */}
            {flow === "capture" && (
              <div className={"s-card"}>
                <label className="form-label">Card Details</label>
                <div id={"card-fields"} />
              </div>
            )}
            {/* End: Card Details */}

            <div className="gap"></div>
            <button
              className="btn btn-primary w-100"
              type="submit"
              disabled={isLoading || (flow === "capture" && !cardComplete)}
              onClick={processPayment}
            >
              {isLoading ? "Loading..." : "Submit"}
            </button>
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
